import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Column } from 'primereact/column'
import { Paginator, type PaginatorPageChangeEvent } from 'primereact/paginator'
import Button from '../../../components/ui/Button'
import type { DataTableRowClickEvent, DataTablePageEvent } from 'primereact/datatable'
import DataTable from '../../../components/ui/DataTable'
import StatusTag from '../../../components/ui/StatusTag'
import Badge from '../../../components/ui/Badge'
import EmptyState from '../../../components/ui/EmptyState'
import TicketHistoryPanel from './TicketHistoryPanel'
import { TICKET_REQUEST_STATUS, type TicketRequestListItem } from '../types'
import { tryCurrency } from '../format'
import { formatDate } from '../../../lib/datetime'
import { useGrants } from '../../../auth/useCurrentUser'
import { GRANTS } from '../../../auth/grants'
import { CARD_VIEW_BREAKPOINT, useMediaQuery } from '../../../lib/useMediaQuery'
import { isCardBodyClick } from '../../../lib/cardClick'

interface TicketTableProps {
  items: TicketRequestListItem[]
  loading: boolean
  page: number
  rows: number
  totalElements: number
  onPage: (page: number, rows: number) => void
  onReview: (id: number) => void
  onApprove: (row: TicketRequestListItem) => void
  onReject: (row: TicketRequestListItem) => void
  /** 037: özel kalem tam-kimlik araması aktifse, satır-açılım geçmiş paneli de bu kanıtı taşır (kendi-dışı satır 404 vermesin). */
  exactPnr?: string
  exactName?: string
}

interface RowActionsProps {
  row: TicketRequestListItem
  onReview: (id: number) => void
  onApprove: (row: TicketRequestListItem) => void
  onReject: (row: TicketRequestListItem) => void
}

/**
 * Satır işlemleri — Onayla/Reddet/İncele **doğrudan buton** olarak çıkar (kullanıcı kararı
 * 2026-08-21 — deadline-requests ekranındaki aynı karardan sonra buraya da uygulandı: önceki kebab
 * (3 nokta) menüsü ekstra bir tıklama gerektiriyordu). Onayla/Reddet statüye göre koşulludur, İncele
 * (detaya gitmenin tek yolu) her durumda görünür. Satır gövdesine tıklama geçmiş akordeonunu
 * açtığından, `tk-acts` sarmalayıcısı (bkz. `TicketTable`'daki `onRowClick`) buton tıklamalarının
 * satırı da açmasını engeller.
 *
 * <p><b>`severity` verilmez:</b> `primereact-overrides.css` "TEXT — nötr gri, asla kırmızı değil"
 * kuralıyla tüm `text` varyantı butonları severity'den bağımsız nötr griye sabitler — Onayla/Reddet
 * yeşil/kırmızı ikon rengiyle ayrışmaz, ikon (`pi-check`/`pi-times`/`pi-eye`) ve tooltip ayrımı yeterlidir.
 */
function RowActions({ row, onReview, onApprove, onReject }: Readonly<RowActionsProps>) {
  const { t } = useTranslation()
  const { hasGrant } = useGrants()
  const statusId = row.status?.id
  // Onayla/Reddet yalnız TICKET_REQUEST_APPROVE yetkisi olan kullanıcıda (FR-016; asıl koruma backend'de).
  const canDecide = hasGrant(GRANTS.TICKET_REQUEST_APPROVE)
  // Statü geçişleri (IS_PROCESSED ile): Beklemede → Onayla+Reddet; Onaylandı → yok;
  // Reddedildi & işlenmemiş → yalnız Onayla; Reddedildi & işlenmiş → yalnız İncele.
  const canApprove =
    canDecide &&
    (statusId === TICKET_REQUEST_STATUS.PENDING ||
      (statusId === TICKET_REQUEST_STATUS.REJECTED && row.isProcessed === false))
  const canReject = canDecide && statusId === TICKET_REQUEST_STATUS.PENDING

  return (
    <span className="tk-acts">
      {canApprove && (
        <Button
          type="button"
          rounded
          text
          icon="pi pi-check"
          aria-label={t('tickets.actions.approve')}
          tooltip={t('tickets.actions.approve')}
          tooltipOptions={{ position: 'bottom' }}
          onClick={() => onApprove(row)}
        />
      )}
      {canReject && (
        <Button
          type="button"
          rounded
          text
          icon="pi pi-times"
          aria-label={t('tickets.actions.reject')}
          tooltip={t('tickets.actions.reject')}
          tooltipOptions={{ position: 'bottom' }}
          onClick={() => onReject(row)}
        />
      )}
      <Button
        type="button"
        rounded
        text
        icon="pi pi-eye"
        aria-label={t('tickets.review')}
        tooltip={t('tickets.review')}
        tooltipOptions={{ position: 'bottom' }}
        onClick={() => onReview(row.id)}
      />
    </span>
  )
}

/**
 * Talep listesi tablosu — PrimeReact DataTable (styled) + paylaşılan ui bileşenleri. 014: çoklu seçim
 * (checkbox/toplu onay) kaldırıldı; yerine **satır genişletme** geldi — satırın başındaki aşağı-ok
 * kontrolüne **veya satır gövdesine** tıklayınca satır altında o talebin İşlem Geçmişi açılır
 * (`TicketHistoryPanel`). **Tek-akordeon:** aynı anda yalnız bir satır açık. Satır gövdesi tıklaması
 * detaya **gitmez** (detaya yalnız "İncele"); sunucu-taraflı sayfalama (lazy).
 */
export default function TicketTable({
  items,
  loading,
  page,
  rows,
  totalElements,
  onPage,
  onReview,
  onApprove,
  onReject,
  exactPnr,
  exactName,
}: Readonly<TicketTableProps>) {
  const { t, i18n } = useTranslation()
  const currency = tryCurrency(i18n.language)
  // Tek-akordeon: en çok bir satır açık (yeni açılınca önceki kapanır).
  const [expanded, setExpanded] = useState<TicketRequestListItem[]>([])
  const expandedId = expanded[0]?.id ?? null

  // Sayfa değişince açık satır sıfırlanır (yeni içerik farklı satırlar — eski seçim sıfırlaması gibi).
  useEffect(() => setExpanded([]), [page])

  const toggleRow = (row: TicketRequestListItem) =>
    setExpanded((prev) => (prev[0]?.id === row.id ? [] : [row]))

  const stop = (e: { stopPropagation: () => void }) => e.stopPropagation()

  const expanderBody = (row: TicketRequestListItem) => {
    const open = expandedId === row.id
    return (
      <Button
        type="button"
        text
        rounded
        className="tk-expander"
        icon={open ? 'pi pi-chevron-down' : 'pi pi-chevron-right'}
        aria-label={t('tickets.expandHistory')}
        aria-expanded={open}
        onClick={(e) => {
          stop(e)
          toggleRow(row)
        }}
      />
    )
  }

  const statusBody = (row: TicketRequestListItem) => (
    <StatusTag statusId={row.status?.id} label={row.status?.name} />
  )
  const amountBody = (row: TicketRequestListItem) =>
    row.ticketPrice == null ? '' : currency.format(row.ticketPrice)
  const dateBody = (row: TicketRequestListItem) => formatDate(row.creDate)

  const actionsBody = (row: TicketRequestListItem) => (
    <RowActions row={row} onReview={onReview} onApprove={onApprove} onReject={onReject} />
  )

  // Telefon/tablet genişliğinde (≤1024px) 8 kolonlu tablo yerine kart listesi — aynı veri/sayfalama/
  // satır-genişletme (geçmiş paneli)/işlemler, yalnız sunum dalı değişir (bkz. ui.css `.ui-card-list`).
  const isMobile = useMediaQuery(CARD_VIEW_BREAKPOINT)

  if (isMobile) {
    return (
      <div className="ui-card-list">
        {loading &&
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="ui-card ui-card--skeleton" aria-hidden />
          ))}
        {!loading && items.length === 0 && <EmptyState title={t('tickets.empty')} icon="pi pi-inbox" />}
        {!loading &&
          items.map((row) => {
            const open = expandedId === row.id
            return (
              <div key={row.id} className="ui-card">
                <div
                  className="ui-card--clickable"
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    // Eylem alanından gelen tıklama geçmiş panelini AÇMAZ; gerekçe: `lib/cardClick`.
                    if (isCardBodyClick(e)) toggleRow(row)
                  }}
                  onKeyDown={(e) => {
                    // Yalnız kartın KENDİSİNE gelen Enter/Space satırı açar: Onayla/Reddet/İncele
                    // butonunda Enter'a basmak satırı aynı anda açıp kapatıyordu (click yolu
                    // yukarıdaki `isCardBodyClick` ile eleniyor ama keydown yukarı kabarcıklanıyor).
                    // Koruma KARTTA durur; eylem sarmalayıcısı handler TAŞIMAZ — `0717f6ca`
                    // sarmalayıcıdaki click-stopPropagation'ı kaldırmıştı (S1082+S6848), sarmalayıcıya
                    // `onKeyDown` eklemek de aynı S6848 gerekçesiyle yasaktır (`c2741715` bunu
                    // netleştirdi); 31.08.2026'da (bu commit) aynı click temizliği buraya da
                    // uygulandı. Masaüstü tablo dalındaki karşılığı `onRowClick` içindeki
                    // `.tk-acts` denetimidir. Emsal: MeetingGroupCard.
                    if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
                      e.preventDefault()
                      toggleRow(row)
                    }
                  }}
                >
                  <div className="ui-card-top">
                    <div className="ui-card-row">
                      <StatusTag statusId={row.status?.id} label={row.status?.name} />
                      {row.type?.name && <Badge tone="neutral">{row.type.name}</Badge>}
                    </div>
                    <span className="ui-card-actions ui-card-actions--compact">
                      <RowActions row={row} onReview={onReview} onApprove={onApprove} onReject={onReject} />
                    </span>
                  </div>
                  <div className="ui-card-head">
                    <span className="ui-card-title">{row.pnr}</span>
                  </div>
                </div>
                <span className="ui-card-sub">{row.nameSurname}</span>
                <div className="ui-card-row" style={{ justifyContent: 'space-between' }}>
                  <div className="ui-card-row">
                    {/* Ayırıcı, ofis adıyla BİRLİKTE koşullu: tek başına render edilirse
                        ofisi olmayan talepte kart "- 01.01.2026" diye sarkan bir tireyle
                        başlıyordu (GitLab Duo bulgusu, MR !170). */}
                    {row.office?.name && (
                      <>
                        <span>{row.office.name}</span>
                        <span>-</span>
                      </>
                    )}
                    <span>{formatDate(row.creDate)}</span>
                  </div>
                  {row.ticketPrice != null && <span style={{ fontWeight: 'bold' }}>{currency.format(row.ticketPrice)}</span>}
                </div>
                {open && (
                  <TicketHistoryPanel ticketId={row.id} exactPnr={exactPnr} exactName={exactName} />
                )}
              </div>
            )
          })}
        {!loading && totalElements > rows && (
          <Paginator
            first={page * rows}
            rows={rows}
            totalRecords={totalElements}
            onPageChange={(e: PaginatorPageChangeEvent) => onPage(e.page, e.rows)}
            template="PrevPageLink CurrentPageReport NextPageLink"
            className="ui-cards-paginator"
          />
        )}
      </div>
    )
  }

  return (
    <DataTable
      value={items}
      loading={loading}
      rowClassName={(row: TicketRequestListItem) => (expandedId === row.id ? 'tk-row-open' : '')}
      onRowClick={(e: DataTableRowClickEvent) => {
        // İşlemler hücresinden (Onayla/Reddet/İncele butonları) gelen tıklama satırı genişletmesin.
        const target = e.originalEvent.target
        if (target instanceof Element && target.closest('.tk-acts')) return
        toggleRow(e.data as TicketRequestListItem)
      }}
      expandedRows={expanded}
      rowExpansionTemplate={(row) => (
        <TicketHistoryPanel
          ticketId={row.id}
          exactPnr={exactPnr}
          exactName={exactName}
        />
      )}
      lazy
      paginator={totalElements > 0}
      rows={rows}
      first={page * rows}
      totalRecords={totalElements}
      onPage={(e: DataTablePageEvent) => onPage(e.page ?? 0, e.rows ?? rows)}
      emptyMessage={<EmptyState title={t('tickets.empty')} icon="pi pi-inbox" />}
    >
      <Column body={expanderBody} headerStyle={{ width: '3rem' }} bodyStyle={{ textAlign: 'center' }} />
      <Column field="pnr" header={t('tickets.columns.pnr')} bodyClassName="tk-cell-strong" />
      <Column field="nameSurname" header={t('tickets.columns.passenger')} />
      <Column header={t('tickets.columns.status')} body={statusBody} />
      <Column
        header={t('tickets.columns.type')}
        body={(r: TicketRequestListItem) => <Badge tone="neutral">{r.type?.name}</Badge>}
      />
      <Column header={t('tickets.columns.date')} body={dateBody} />
      <Column header={t('tickets.columns.office')} body={(r: TicketRequestListItem) => r.office?.name} />
      <Column header={t('tickets.columns.amount')} body={amountBody} bodyClassName="tk-cell-strong" />
      <Column
        header={t('tickets.columns.actions')}
        body={actionsBody}
        // En fazla 3 ikon-only buton (Onayla+Reddet+İncele) yan yana — tek kebab ikonundan (4rem) büyütüldü.
        headerStyle={{ width: '7rem' }}
        bodyStyle={{ textAlign: 'center' }}
      />
    </DataTable>
  )
}
